import type { TOC } from '@ember/component/template-only';
import Heading from './heading';
import Text from './text';
import type { ComponentLike } from '@glint/template';

interface SectionHeaderSignature {
  Args: {
    /**
     * Icon component to display
     */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /**
     * Title text
     */
    title: string;
    /**
     * Optional subtitle/description
     */
    subtitle?: string;
    /**
     * Icon color class
     * @default "tio-fg-secondary"
     */
    iconClass?: string;
    /**
     * Icon size
     * @default 24
     */
    iconSize?: number;
    /**
     * Heading level
     * @default 2
     */
    level?: 1 | 2 | 3 | 4;
    /**
     * Utility override for the standard CSS-owned section-header spacing.
     */
    marginClass?: string;
  };
  Blocks: {
    /**
     * Optional content on the right side (e.g., badge, button)
     */
    trailing?: [];
  };
  Element: HTMLDivElement;
}

/** Section heading with an icon and optional trailing action or badge block. */
const SectionHeader: TOC<SectionHeaderSignature> = <template>
  <div
    data-test-section-header
    class="tio-section-header {{if @marginClass @marginClass}}"
    ...attributes
  >
    <@icon
      @size={{if @iconSize @iconSize 24}}
      class="{{unless @iconSize 'tio-icon-default'}}
        {{if @iconClass @iconClass 'tio-fg-secondary'}}"
    />
    <div class="tio-section-header__content">
      <Heading
        data-test-section-header-title
        @level={{if @level @level 2}}
        @role="section-title"
        class="tio-section-header__title"
      >
        {{@title}}
      </Heading>
      {{#if @subtitle}}
        <Text data-test-section-header-subtitle @variant="muted" @role="body-secondary">
          {{@subtitle}}
        </Text>
      {{/if}}
    </div>
    {{#if (has-block "trailing")}}
      <div data-test-section-header-trailing>
        {{yield to="trailing"}}
      </div>
    {{/if}}
  </div>
</template>;

export default SectionHeader;
export type { SectionHeaderSignature };
